import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AppropriatenessTestMarginWipeoutPriceMove } from '@/features/futures/schemas/appropriateness-test';
import { usePromise } from '@/hooks/use-promise';
import { FUTURES_APPROPRIATENESS_MARGIN_WIPEOUT_PRICE_MOVE_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.marginWipeoutPriceMove.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.marginWipeoutPriceMove.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend.displayName = 'FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend';

const FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove = () => {
  const form = useFormContext<Partial<AppropriatenessTestMarginWipeoutPriceMove>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="marginWipeoutPriceMove"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.marginWipeoutPriceMove.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.marginWipeoutPriceMove.title')}
              onValueChange={(value) => {
                field.onChange(value);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_MARGIN_WIPEOUT_PRICE_MOVE_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.marginWipeoutPriceMove.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove.displayName = 'FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove';
FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove.isAutoNextStep = true;

export { FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove, FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend };
